/**
 * WatchPage.tsx — страница просмотра видео (neobrutalism).
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchVideo, getStreamUrl } from '../api/client';
import { VideoPlayer } from '../components/VideoPlayer';
import { Button } from '../components/ui/button';
import { ChevronLeft } from 'lucide-react';
import type { Video } from '../types';

export const WatchPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [video, setVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) { setError('Некорректный ID видео'); setLoading(false); return; }

    void (async () => {
      try {
        const data = await fetchVideo(id);
        setVideo(data);
      } catch {
        setError('Видео не найдено');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-t-transparent border-foreground rounded-full animate-spin" />
          <p className="text-foreground text-sm font-mono font-bold">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4 border-2 border-foreground bg-card shadow-neo p-8">
          <p className="text-destructive font-mono font-black text-lg">{error ?? 'Ошибка'}</p>
          <Button variant="outline" onClick={() => navigate('/')}>На главную</Button>
        </div>
      </div>
    );
  }

  if (video.status !== 'ready' || !video.streamUrl) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4 border-2 border-foreground bg-card shadow-neo p-8">
          {video.status === 'processing' ? (
            <>
              <div className="w-10 h-10 border-2 border-t-transparent border-foreground rounded-full animate-spin mx-auto" />
              <p className="text-foreground font-bold">Видео ещё обрабатывается...</p>
            </>
          ) : (
            <>
              <p className="text-destructive font-black text-lg">Ошибка обработки видео</p>
              {video.error && (
                <p className="text-destructive text-sm font-mono border-l-2 border-destructive pl-2 text-left">
                  {video.error}
                </p>
              )}
            </>
          )}
          <Button variant="outline" onClick={() => navigate('/')}>На главную</Button>
        </div>
      </div>
    );
  }

  const streamUrl = getStreamUrl(video.streamUrl, video.visibility === 'private');

  return (
    <div className="min-h-screen bg-background">
      {/* Навбар */}
      <header className="sticky top-0 z-20 border-b-2 border-foreground bg-primary text-primary-foreground">
        <div className="mx-auto flex min-h-14 max-w-5xl items-center gap-3 px-3 py-2 sm:gap-4 sm:px-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="font-bold gap-1">
            <ChevronLeft className="w-4 h-4" />
            Назад
          </Button>
          <div className="hidden h-5 w-0.5 bg-primary-foreground sm:block" />
          <div className="flex-1 min-w-0">
            <h1 className="text-primary-foreground font-black text-base truncate">{video.title}</h1>
            <p className="text-primary-foreground/70 text-xs font-mono">HLS · ABR · adaptive bitrate</p>
          </div>
          <span className="text-sm font-black hidden sm:block">
            <span className="bg-foreground text-primary px-2 py-0.5 font-mono text-xs">ABR</span>
            <span className="text-primary-foreground/70 ml-1 text-xs">streaming</span>
          </span>
        </div>
      </header>

      {/* Плеер */}
      <main className="mx-auto max-w-5xl px-2 py-4 sm:px-6 sm:py-6">
        <VideoPlayer src={streamUrl} title={video.title} videoId={video.id} />
      </main>
    </div>
  );
};
